import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import cx from 'classnames';

import { useSprintProductReportsBySprint } from '@data';
import { Loader } from '@ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  selectItemsByValue,
} from '@ui/components/ui/select';
import { useOrganizationId } from '@app/utils/hooks';

import { cardHeaderClass, cardHeaderTitleClass, cardShellClass, tableFrameClass } from '@app/constants/styles';
import { peopleAcross, rateIn, readBudget, teamAt, type VersionTeam } from './budget';
import {
  outOfPocketLines,
  deviations,
  quartersWithin,
  type HoursBySprint,
  type InsightSprint,
  type RateAt,
  reportedWithBoughtIn,
} from './insights';
import { fractionalRate, isFractional, readRoster } from './fractional';
import { formatShortDay } from './productVersions';
import { readQuarterCosts } from './quarterCosts';
import { biggestFirst, peopleLines } from './productInsightsCards';
import BudgetYearHeader from './BudgetYearHeader';
import DeviationRow from './DeviationRow';
import PeopleTimeCard from './PeopleTimeCard';
import TabArrow from './TabArrow';
import TopOutOfPocketCard from './TopOutOfPocketCard';
import { gridHeadText, gridHeadTint } from './sprintGrid';
import { useSettledSprints } from './useSettledSprints';
import { FIRST_MONEY_YEAR, noSprintsKey, useYearChoice } from './yearChoice';

const card = cardShellClass;
const cardTitle = 'text-base font-semibold text-foreground';
const cardHint = 'text-sm text-muted-foreground';

/** The worst five; everyone is on the Spend tab, behind this card's arrow. */
const WORST = 5;

/** Waiting on the year's reports, unable to read them, nothing to show, or the figures themselves. */
const stateOf = (loading: boolean, failed: boolean, empty: boolean): 'loading' | 'failed' | 'empty' | 'ready' => {
  if (loading) return 'loading';
  // Before empty: an unread year subtracts to a whole budget of gap, which ranks as a real deviation.
  if (failed) return 'failed';
  if (empty) return 'empty';
  return 'ready';
};

/** Days, not the instants the API sends: an instant sorts after the day it falls on. */
const asColumn = (sprint: { id: string; number: number; startDate: string }): InsightSprint => ({
  id: sprint.id,
  number: sprint.number,
  startDate: sprint.startDate.slice(0, 10),
});

/** On the current year. */
const InsightsTab = ({ teams }: { teams: VersionTeam[] }): JSX.Element => {
  const { t } = useTranslation();
  const { productId = '' } = useParams<{ productId: string }>();
  const organizationId = useOrganizationId();
  const { sprints, years, year, yearSprints, isLoading } = useYearChoice();
  const [deviationSprintId, setDeviationSprintId] = useState<string | null>(null);

  // A local day; `en-CA` is how the days compare.
  const today = new Date().toLocaleDateString('en-CA');

  // Everyone who has been on the team, priced under whichever version covers each sprint.
  const people = useMemo(() => peopleAcross(teams), [teams]);
  // Bought-in people carry their own rate, read from every year's roster.
  const boughtIn = useMemo(() => years.flatMap(each => readRoster(productId, each)), [productId, years]);
  const rateOver = (span: InsightSprint[]): RateAt => {
    const byId = Object.fromEntries(span.map(sprint => [sprint.id, teamAt(teams, sprint.startDate)]));
    return (sprintId, personId) =>
      isFractional(boughtIn, personId) ? fractionalRate(boughtIn, personId) : rateIn(byId[sprintId], personId);
  };

  const yearColumns = useMemo(() => yearSprints.map(asColumn), [yearSprints]);

  const yearReports = useSprintProductReportsBySprint(yearSprints, productId);

  const yearReported = useMemo<HoursBySprint>(
    () => reportedWithBoughtIn(yearSprints, yearReports.bySprint, productId),
    [yearSprints, yearReports.bySprint, productId],
  );

  const settled = useSettledSprints(organizationId, productId);
  const { closedSprintIds } = settled;

  if (isLoading) return <Loader />;
  if (!yearColumns.length) return <p className={cardHint}>{t(noSprintsKey(sprints), { year: FIRST_MONEY_YEAR })}</p>;

  // Only sprints that have started: one yet to run would read as a whole budget nobody worked.
  const runColumns = yearColumns.filter(sprint => sprint.startDate <= today);
  const yearBudgeted: HoursBySprint = Object.fromEntries(
    runColumns.map(sprint => [sprint.id, readBudget(productId, sprint.id)?.hours ?? {}]),
  );
  const yearRate = rateOver(runColumns);

  // Closed sprints only: an open one is half-reported, and a client has not been sent it.
  const closedColumns = runColumns.filter(sprint => closedSprintIds.includes(sprint.id));
  const deviationSprint =
    closedColumns.find(sprint => sprint.id === deviationSprintId) ?? closedColumns[closedColumns.length - 1];
  const sprintOptions = [...closedColumns].reverse().map(sprint => ({
    value: sprint.id,
    label: t('insights.sow.sprintDay', { number: sprint.number, day: formatShortDay(sprint.startDate) }),
  }));
  /** The same subtraction as the year's, over one sprint. Worst first, and only the worst few are shown. */
  const sprintDeviations = deviationSprint
    ? deviations(people, [deviationSprint], yearBudgeted, yearReported, yearRate)
    : [];

  // A year whose sprints are all still ahead is not a product nobody is on, and a team is not an estimate.
  const emptyNoteKey = (): string => {
    if (!runColumns.length) return 'insights.offPlan.notStarted';
    if (!closedColumns.length) return 'insights.offPlan.notClosed';
    return people.length ? 'insights.offPlan.notEstimated' : 'insights.offPlan.none';
  };
  const emptyNote = t(emptyNoteKey(), { year });
  const offPlanState = stateOf(
    yearReports.isLoading || settled.isLoading,
    yearReports.isError || settled.isError,
    !sprintDeviations.length,
  );

  /** On the year, not a statement of work's span: they sit under the year's own grid. */
  const yearQuarters = quartersWithin(`${year}-01-01`, `${year}-12-31`);
  const yearCosts = { [year]: readQuarterCosts('spend', productId, year) };
  const yearBills = biggestFirst(outOfPocketLines(yearQuarters, yearCosts));
  // The team, not what was bought in: this card is about the people the company employs.
  const personLines = peopleLines(people, closedColumns, yearReported);

  /** The gap alone: at half the tab's width, the figures it is made of crowded out the names. */
  const deviators = (
    <section className={card}>
      {/* No sentence under the title: the picker beside it says which sprint this is. */}
      <div className={cardHeaderClass}>
        <h2 className={cx(cardTitle, cardHeaderTitleClass)}>{t('insights.offPlan.title')}</h2>
        <div className="flex shrink-0 items-center gap-2">
          {deviationSprint && (
            <Select
              value={deviationSprint.id}
              onValueChange={setDeviationSprintId}
              items={selectItemsByValue(sprintOptions)}
              aria-label={t('insights.offPlan.sprint')}
            >
              <SelectTrigger size="sm" className="shrink-0 bg-card shadow-xs" aria-label={t('insights.offPlan.sprint')}>
                <SelectValue />
              </SelectTrigger>
              {/* Newest first: the sprint being asked about is nearly always one of the last few. */}
              <SelectContent
                className="min-w-0"
                style={{ maxHeight: 'min(16rem, var(--available-height))' }}
                align="end"
                alignItemWithTrigger={false}
              >
                {sprintOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {/* Costs, opened on the forecast sprint by sprint. */}
          <TabArrow tab="costs" scrollTo="spend-workforce" />
        </div>
      </div>
      {offPlanState === 'loading' && (
        <div className="px-5 max-md:px-4 pb-5">
          <Loader />
        </div>
      )}
      {offPlanState === 'failed' && (
        <p className="px-5 max-md:px-4 pb-5 text-sm text-muted-foreground">{t('cardReadFailed')}</p>
      )}
      {offPlanState === 'empty' && <p className="px-5 max-md:px-4 pb-5 text-sm text-muted-foreground">{emptyNote}</p>}
      {offPlanState === 'ready' && (
        <div className={cx(tableFrameClass, 'mb-5')}>
          <table className="w-full table-fixed border-separate border-spacing-0 [&_tr>*:last-child]:border-r-0 [&>tbody>tr:last-child>*]:border-b-0">
            <thead>
              <tr>
                {/* The rank column is as narrow as its chip; the name takes what is left. */}
                <th scope="col" className={cx(gridHeadText, gridHeadTint, 'w-14 text-left')}>
                  {t('insights.offPlan.rank')}
                </th>
                <th scope="col" className={cx(gridHeadText, gridHeadTint, 'text-left')}>
                  {t('member')}
                </th>
                <th scope="col" className={cx(gridHeadText, gridHeadTint, 'w-32 text-right')}>
                  {t('insights.offPlan.deviation')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sprintDeviations.slice(0, WORST).map((row, index) => (
                <DeviationRow key={row.personId} row={row} rank={index + 1} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );

  return (
    <div className="space-y-8">
      {/* The heading and the first cards are one block, matching the gap over the tabs. */}
      <div className="space-y-4">
        <BudgetYearHeader title={t('insights.yearTitle', { year })} />

        {/* Who spent the time, and what was bought in on top; two thirds to the chart. */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <PeopleTimeCard
            lines={personLines}
            sprints={closedColumns}
            year={year}
            isLoading={yearReports.isLoading || settled.isLoading}
            isError={yearReports.isError || settled.isError}
            className="lg:col-span-2"
            action={<TabArrow tab="costs" scrollTo="spend-workforce" />}
          />
          <TopOutOfPocketCard
            lines={yearBills}
            year={year}
            action={<TabArrow tab="costs" scrollTo="spend-out-of-pocket" />}
          />
        </div>
      </div>

      {/* Against the budget, kept low on the page: the plan is the PM's estimate, not a promise. */}
      {deviators}
    </div>
  );
};

export default InsightsTab;
